import React, { useEffect, useState } from 'react';
import { errorService } from '@root/app/shared/new-services/newError.service';
import { notificationService } from '@root/app/shared/new-services/notification.service';
import {
    Dialog,
    DialogActions,
    DialogContent,
    Select,
    MenuItem,
    FormControl,
    Typography,
    Avatar,
    Grid,
    FormHelperText,
    Box,
    Switch,
    SelectChangeEvent,
    Stack,
    OutlinedInput
} from '@mui/material';
import CloseableDialogTitle from '@root/app/shared/new-components/hf-closeable-dialog-title/CloseableDialogTitle';
import HfButton from '@root/app/shared/new-components/hf-button/HfButton';
import { useTranslation } from '@root/app/shared/hooks/useTranslation';
import { reloadCurrentRoute } from '@root/app/shared/navigation/navigation.service';
import { Upload } from '@mui/icons-material';
import { authService } from '@root/app/shared/new-services/auth.service';
import { ManagementFocus } from '@root/api';
import { FOCUS_ORDER } from '@root/app/shared/utils/managementFocus.utils';
import HfFocusOptionLabel from '@root/app/shared/new-components/hf-focus/HfFocusOptionLabel';
import HfFocusValue from '@root/app/shared/new-components/hf-focus/HfFocusValue';

// type definitions
interface NotificationSetting {
    type: string;
    frequency: 'IMMEDIATELY' | 'NEVER';
}

interface User {
    name: string;
    role?: string;
    managementFocus?: ManagementFocus;
    password?: string;
    language: string;
    pushNotificationSettings: NotificationSetting[];
    mailNotificationSettings: NotificationSetting[];
}

interface NotificationType {
    type: string;
    label: string;
    mailAvailableFrequency: string[];
    pushAvailableFrequency: string[];
}

interface HfUserProfileSettingsProps {
    show: boolean;
    onClose: () => void;
}


const HfUserProfileSettings: React.FC<HfUserProfileSettingsProps> = ({ show, onClose }) => {
    const { t, getCurrentLanguage } = useTranslation();

    const [user, setUser] = useState<User | null>(null);
    const [newpassword, setNewpassword] = useState<string>('');
    const [newpasswordbis, setNewpasswordbis] = useState<string>('');
    const [changed, setChanged] = useState<boolean>(false);
    const [actualLang, setActualLang] = useState<string>(getCurrentLanguage());
    const [errored, setErrored] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>('');

    const notificationTypes: NotificationType[] = [
        {
            type: 'PRIVATE_COMMENT_RECEIVED',
            label: t('PRIVATE_COMMENT_RECEIVED'),
            mailAvailableFrequency: ['IMMEDIATELY', 'NEVER'],
            pushAvailableFrequency: ['IMMEDIATELY', 'NEVER']
        },
        {
            type: 'INAPPROPRIATE_COMMENT_RECEIVED',
            label: t('INAPPROPRIATE_COMMENT_RECEIVED'),
            mailAvailableFrequency: ['IMMEDIATELY', 'NEVER'],
            pushAvailableFrequency: ['IMMEDIATELY', 'NEVER']
        },
        {
            type: 'COMMENT_RECEIVED',
            label: t('COMMENT_RECEIVED'),
            mailAvailableFrequency: ['IMMEDIATELY', 'NEVER'],
            pushAvailableFrequency: ['IMMEDIATELY', 'NEVER']
        },
        {
            type: 'QUESTION_FINISHED',
            label: t('QUESTION_FINISHED'),
            mailAvailableFrequency: ['IMMEDIATELY', 'NEVER'],
            pushAvailableFrequency: ['IMMEDIATELY', 'NEVER']
        }
    ];

    useEffect(() => {
        if (show) {
            authService.me((err, me) => {
                if (me?.companyUser) {
                    setUser({ ...me.companyUser });
                }
            });
        }
    }, [show]);


    useEffect(() => {
        setActualLang(getCurrentLanguage());
    }, [getCurrentLanguage]);

    useEffect(() => {
        if (changed) {
            if (newpassword.length <= 5) {
                setErrored(true);
                setErrorMsg(t('EDIT_PASS_TO_SHORT'));
            } else if (newpassword !== newpasswordbis) {
                setErrored(true);
                setErrorMsg(t('EDIT_PASS_ERROR'));
            } else {
                setErrored(false);
                setErrorMsg('');
            }
        }
    }, [newpassword, newpasswordbis, changed]);

    const setUserImage = (files: FileList | null): void => {
        if (!files || files.length === 0) { return; }

        authService.setUserImage(files[0], (updatedUser: User) => {
            setUser(updatedUser);
            notificationService.success(t('PROFILE_IMG_UPDATED'));
        }, () => undefined, () => undefined);
    };

    const updateUser = (): void => {
        if (!user) { return; }

        const updatedUser = { ...user };

        if (newpassword !== '') {
            updatedUser.password = newpassword;
        }

        updatedUser.language = actualLang;

        // GENERIC is what the company set while the person hadn't chosen: only a real focus can be sent back.
        if (updatedUser.managementFocus === ManagementFocus.Generic) {
            delete updatedUser.managementFocus;
        }

        authService.updateUser(updatedUser, (updatedUserData: User) => {
            setUser(updatedUserData);
            notificationService.success(t('PROFILE_UPDATED'));
            onClose();
            reloadCurrentRoute();
        }, (response: any) => {
            errorService.showErrorToast(response);
        });
    };

    const cancel = (): void => {
        onClose();
    };

    const renderNotificationSetting = (notificationType: NotificationType): React.ReactElement => {
        if (!user) { return <></>; }

        const userPushNotificationSetting = user.pushNotificationSettings.find((n) => n.type === notificationType.type);
        const userMailNotificationSetting = user.mailNotificationSettings.find((n) => n.type === notificationType.type);

        if (!userPushNotificationSetting || !userMailNotificationSetting) { return <></>; }

        return (
            <Grid container spacing={2} sx={{
                display: 'flex',
                alignItems: 'center'
            }}>
                <Grid item xs={8}>
                    <Typography variant='body3' fontWeight='bold'>{notificationType.label}</Typography>
                </Grid>
                <Grid item xs={2} textAlign='center'>
                    <FormControl fullWidth size={'small'}>
                        <Switch
                            checked={userPushNotificationSetting.frequency === 'IMMEDIATELY'}
                            onChange={(event) => {
                                if (!user) { return; }

                                const updatedPushSettings = user.pushNotificationSettings.map(setting =>
                                    setting.type === notificationType.type
                                        ? { ...setting, frequency: event.target.checked ? 'IMMEDIATELY' : 'NEVER' }
                                        : setting
                                );
                                setUser({ ...user, pushNotificationSettings: updatedPushSettings });
                            }}
                        />
                    </FormControl>
                </Grid>
                <Grid item xs={2} textAlign='center'>
                    <FormControl fullWidth size={'small'}>
                        <Switch
                            checked={userMailNotificationSetting.frequency === 'IMMEDIATELY'}
                            onChange={(event) => {
                                if (!user) { return; }

                                const updatedMailSettings = user.mailNotificationSettings.map(setting =>
                                    setting.type === notificationType.type
                                        ? { ...setting, frequency: event.target.checked ? 'IMMEDIATELY' : 'NEVER' }
                                        : setting
                                );
                                setUser({ ...user, mailNotificationSettings: updatedMailSettings });
                            }}
                        />
                    </FormControl>
                </Grid>
            </Grid>
        );
    };

    if (!show || !user) {
        return null;
    }

    return (
        <Dialog open={show} onClose={cancel}>
            <CloseableDialogTitle title={t('MY_PROFILE')} onClose={cancel} />
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={8}>

                        <form noValidate autoComplete='off'>
                            <Stack gap={2}>
                                <Stack gap={1}>
                                    <Typography variant='h5' fontWeight='bold'>{t('MY_PROFILE_USER_NAME')}</Typography>
                                    <Typography variant='body3' color='text.secondary'>{t('MY_PROFILE_USER_NAME_DESCRIPTION')}</Typography>
                                    <OutlinedInput
                                        fullWidth
                                        id='userName'
                                        value={user.name}
                                        onChange={(e) => {
                                            setUser({ ...user, name: e.target.value });
                                            setChanged(true);
                                        }}
                                        aria-describedby='userName-text'
                                    />
                                </Stack>

                                <Stack gap={1}>
                                    <Typography variant='h5' fontWeight='bold'>{t('NEW_PASS')}</Typography>
                                    <Typography variant='body3' color='text.secondary'>{t('NEW_PASS_DESCRIPTION')}</Typography>
                                    <OutlinedInput
                                        fullWidth
                                        id='newUserPassword'
                                        value={newpassword}
                                        placeholder={'******'}
                                        type='password'
                                        onChange={(e) => {
                                            setChanged(true);
                                            setNewpassword(e.target.value);
                                        }}
                                        aria-describedby='newUserPassword-text'
                                        error={!!errorMsg}
                                    />
                                </Stack>

                                <Stack gap={1}>
                                    <Typography variant='h5' fontWeight='bold'>{t('REP_NEW_PASS')}</Typography>
                                    <OutlinedInput
                                        fullWidth
                                        id='userPasswordBiss'
                                        value={newpasswordbis}
                                        placeholder={'******'}
                                        type='password'
                                        onChange={(e) => {
                                            setChanged(true);
                                            setNewpasswordbis(e.target.value);
                                        }}
                                        aria-describedby='userPasswordBiss-text'
                                        error={!!errorMsg}
                                    />
                                    {errorMsg && (
                                        <FormHelperText id='userPasswordBiss-text' error={!!errorMsg}>
                                            {errorMsg}
                                        </FormHelperText>
                                    )}
                                </Stack>
                            </Stack>
                        </form>
                    </Grid>
                    <Grid item xs={4}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            height: '100%'
                        }}>
                            <Avatar
                                alt={user.name}
                                src={authService.getUserImage()}
                                sx={{ width: 100, height: 100, mb: 2 }}
                            >
                                {user.name[0]?.toUpperCase() || 'HF'}
                            </Avatar>
                            <input
                                type='file'
                                style={{ display: 'none' }}
                                id='raised-button-file'
                                onChange={(e) => setUserImage(e.target.files)}
                            />
                            <label htmlFor='raised-button-file'>
                                <HfButton size='small' component='span' intent='secondary' startIcon={<Upload />}>
                                    {t('CHANGE_PROFILE_PIC')}
                                </HfButton>
                            </label>
                        </Box>
                    </Grid>
                    {(user.role === 'MANAGER' || user.role === 'ADMIN') && (
                        <Grid item xs={12}>
                            <Stack gap={1}>
                                <Typography variant='h5' fontWeight='bold'>{t('MANAGEMENT_FOCUS')}</Typography>
                                <Typography variant='body3' color='text.secondary'>{t('MY_PROFILE_MANAGEMENT_FOCUS_DESCRIPTION')}</Typography>
                                {user.managementFocus === ManagementFocus.Generic && (
                                    <Typography variant='caption' color='warning.main'>{t('MY_PROFILE_MANAGEMENT_FOCUS_GENERIC')}</Typography>
                                )}
                                <FormControl fullWidth size={'small'}>
                                    <Select<ManagementFocus>
                                        value={user.managementFocus === ManagementFocus.Generic ? ('' as ManagementFocus) : (user.managementFocus || ('' as ManagementFocus))}
                                        onChange={(e) => setUser({ ...user, managementFocus: e.target.value as ManagementFocus })}
                                        renderValue={(focus) => <HfFocusValue focus={focus} />}
                                        MenuProps={{ PaperProps: { sx: { maxWidth: 420 } } }}
                                    >
                                        {FOCUS_ORDER.map((focus) => (
                                            <MenuItem key={focus} value={focus} sx={{ whiteSpace: 'normal' }}>
                                                <HfFocusOptionLabel focus={focus} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Stack gap={1}>
                            <Typography variant='h5' fontWeight='bold'>{t('LANGUAGE')}</Typography>
                            <Typography variant='body3' color='text.secondary'>{t('MY_PROFILE_LANGUAGE_DESCRIPTION')}</Typography>
                            <FormControl fullWidth size={'small'}>
                                <Select
                                    value={actualLang}
                                    onChange={(e: SelectChangeEvent<string>) => { setActualLang(e.target.value); }}
                                >
                                    <MenuItem value={'en'}>English</MenuItem>
                                    <MenuItem value={'es'}>Español</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                    </Grid>
                    <Grid item xs={12}>
                        <Stack gap={1}>
                            <Typography variant='h5' fontWeight='bold'>{t('USER_PROFILE_NOTIFICATION_OPTIONS')}</Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Grid container spacing={1} sx={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        <Grid item xs={8}>
                                            <Typography variant='body3' color='text.secondary'>{t('USER_PROFILE_NOTIFICATION_OPTIONS_DESCRIPTION')}</Typography>
                                        </Grid>
                                        <Grid item xs={2} textAlign='center'>
                                            <Typography variant='body3' color='text.secondary'>{t('USER_PROFILE_NOTIFICATION_PUSH')}</Typography>
                                        </Grid>
                                        <Grid item xs={2} textAlign='center'>
                                            <Typography variant='body3' color='text.secondary'>{t('USER_PROFILE_NOTIFICATION_MAIL')}</Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {notificationTypes?.map((notification) => (
                                    <Grid item xs={12} key={notification.type}>
                                        {renderNotificationSetting(notification)}
                                    </Grid>
                                ))}
                            </Grid>
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <HfButton
                    onClick={updateUser}
                    intent='primary'
                    disabled={errored || user?.name === ''}
                >
                    {t('SAVE')}
                </HfButton>
            </DialogActions>
        </Dialog>
    );
};

export default HfUserProfileSettings;
